import React from "react";
import { gql, useQuery } from "@apollo/client";
import {
  Customer,
  FindRecentOfferRedemptionsByIdQuery,
  FindRecentOfferRedemptionsByIdQueryVariables,
} from "../../../../schemaTypes";

export const FIND_RECENT_OFFER_REDEMPTIONS_BY_ID = gql`
  query FindRecentOfferRedemptionsById(
    $offerId: String!
    $page: Int!
    $pageSize: Int!
    $searchText: String
    $duration: String
  ) {
    findRecentOfferRedemptionsById(
      offerId: $offerId
      page: $page
      pageSize: $pageSize
      searchText: $searchText
      duration: $duration
    ) {
      data {
        customer {
          id
          firstname
          lastname
          username
          community {
            id
            name
            alias
          }
        }
        redeemedCode
        redemptionDate
      }
      redeemedCodes
      remainingCodes
      totalOfferViews
      total
      page
      pageSize
      totalPages
    }
  }
`;

interface RedemptionEntry {
  customer: Customer;
  redeemedCode: string;
  redemptionDate?: string | null;
}

interface WithFindRecentOfferRedemptionsById {
  redemptions: RedemptionEntry[];
  loading: boolean;
  loadMore: () => void;
  hasMore: boolean;
  page: number;
  pageSize: number;
  total: number;
  totalPages: number;
  redeemedCodes: number;
  remainingCodes: number;
  totalOfferViews: number;
}

interface Props {
  variables: FindRecentOfferRedemptionsByIdQueryVariables;
  children: (data: WithFindRecentOfferRedemptionsById) => React.ReactElement | null;
}

export const FindRecentOfferRedemptionsById: React.FC<Props> = ({ children, variables }) => {
  const { data, loading, fetchMore } = useQuery<
    FindRecentOfferRedemptionsByIdQuery,
    FindRecentOfferRedemptionsByIdQueryVariables
  >(
    FIND_RECENT_OFFER_REDEMPTIONS_BY_ID,
    {
      variables,
      notifyOnNetworkStatusChange: true,
    }
  );

  const redemptions = data?.findRecentOfferRedemptionsById?.data || [];
  const page = data?.findRecentOfferRedemptionsById?.page || variables.page || 1;
  const pageSize = data?.findRecentOfferRedemptionsById?.pageSize || variables.pageSize || 10;
  const total = data?.findRecentOfferRedemptionsById?.total || 0;
  const totalPages = data?.findRecentOfferRedemptionsById?.totalPages || 0;
  const hasMore = page < totalPages;
  const redeemedCodes = data?.findRecentOfferRedemptionsById?.redeemedCodes || 0;
  const remainingCodes = data?.findRecentOfferRedemptionsById?.remainingCodes || 0;
  const totalOfferViews = data?.findRecentOfferRedemptionsById?.totalOfferViews || 0;

  const loadMore = React.useCallback(() => {
    if (!hasMore) return;
    fetchMore({
      variables: {
        ...variables,
        page: page + 1,
      },
      updateQuery: (prev, { fetchMoreResult }) => {
        if (!fetchMoreResult || !fetchMoreResult.findRecentOfferRedemptionsById) {
          return prev;
        }
        return {
          ...fetchMoreResult,
          findRecentOfferRedemptionsById: {
            ...fetchMoreResult.findRecentOfferRedemptionsById,
            data: [
              ...(prev?.findRecentOfferRedemptionsById?.data || []),
              ...(fetchMoreResult.findRecentOfferRedemptionsById.data || [])
            ]
          }
        };
      }
    });
  }, [fetchMore, variables, page, hasMore]);

  return children({
    redemptions: redemptions as RedemptionEntry[],
    loading,
    loadMore,
    hasMore,
    page,
    pageSize,
    total,
    totalPages,
    redeemedCodes,
    remainingCodes,
    totalOfferViews
  });
};